import { ProductCollectionFilter, applyProductCollectionFilter } from "../ProductSlider/productCollection.shared";
import React from "react";
import { LinkComponent } from "../../../types";
import { ArrowRight } from "lucide-react";
import ProductEmptyState from "../ProductEmptyState/ProductEmptyState";
import { productItemTilesBaseStyles, productItemTilesNeutralStyles } from "./productItemTiles.baseStyles";
import { productItemTilesTokens, ProductItemTilesStyle } from "./productItemTiles.tokens";
import { getBadgeText, getCtaText, isValidCtaUrl } from "./productItemTiles.helpers";

export type { ProductItemTilesStyle } from "./productItemTiles.tokens";

export type ProductItemTileItem = {
    id: string;
    slug: string;
    name: string;
    internalLink: string;
    accentColor?: string;
    accentDarkColor?: string;
    parentId?: string;
    categoryId?: string;
    productId?: string;
    sortOrder?: number;
    flags?: {
        isFeatured?: boolean;
        isClearance?: boolean;
        isAusMade?: boolean;
        isInStock?: boolean;
    };
    basic?: {
        tags?: string[];
        active?: boolean;
    };
    featureImage?: {
        original?: string;
        thumbnail?: string;
        alt?: string;
    };
    pricing?: Array<{
        label?: string;
        price?: number | null;
    }>;
};

type ProductItemTilesProps = {
    items: ProductItemTileItem[];
    Link: LinkComponent;
    title?: string;
    eyebrow?: string;
    description?: string;
    tileClasses?: string;
    filter?: ProductCollectionFilter;
    emptyTitle?: string;
    emptyDescription?: string;
    style?: ProductItemTilesStyle;
    ctaText?: string;
    ctaUrl?: string;
};

export const tileBaseClasses = `
  group
  relative
  flex
  aspect-square
  min-w-[200px]
  flex-col
  overflow-hidden
  rounded-2xl
  border border-neutral-200
  bg-white
  shadow-md
  transition-all duration-300
  hover:-translate-y-1
  hover:shadow-xl
`;

function formatPrice(price?: number | null) {
    if (typeof price !== "number") return null;
    return `$${price.toLocaleString("en-AU")}`;
}

export function ProductItemTile({
                                    item,
                                    Link,
                                }: {
    item: ProductItemTileItem;
    Link: LinkComponent;
}) {
    const imageUrl = item.featureImage?.thumbnail || item.featureImage?.original || "";
    const imageAlt = item.featureImage?.alt || item.name;

    const firstPrice = item.pricing?.[0];
    const isZeroPrice = firstPrice?.price === 0;
    const formattedPrice = formatPrice(firstPrice?.price);

    const pricingDisplay = isZeroPrice
        ? {
            label: "Contact For Pricing",
            value: "View Details",
        }
        : {
            label: firstPrice?.label ?? "Starting From",
            value: formattedPrice ?? "Enquire",
        };

    return (
        <Link href={item.internalLink} className={tileBaseClasses}>
            <div className="relative h-full w-full overflow-hidden bg-neutral-100">
                {!!imageUrl && (
                    <img
                        src={imageUrl}
                        alt={imageAlt}
                        className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-[1.03]"
                    />
                )}

                <div
                    className="absolute inset-x-0 bottom-0 h-28"
                    style={{
                        background:
                            "linear-gradient(to top, rgba(0,0,0,0.82), rgba(0,0,0,0.48), transparent)",
                    }}
                />

                <div
                    className="absolute inset-x-0 bottom-0 flex flex-col gap-1 p-3"
                    style={{
                        color: "#ffffff",
                        fontFamily: "inherit",
                    }}
                >
                    <h3
                        className="m-0 line-clamp-2"
                        style={{
                            margin: 0,
                            padding: 0,
                            color: "#ffffff",
                            fontSize: "14px",
                            fontWeight: 600,
                            lineHeight: 1.25,
                            letterSpacing: "0",
                            textTransform: "none",
                            fontFamily: "inherit",
                            textDecoration: "none",
                            textShadow: "0 1px 2px rgba(0,0,0,0.5)",
                        }}
                    >
                        {item.name}
                    </h3>

                    <div className="flex items-end justify-between gap-2 pt-1">
                        <div className="min-w-0">
                            {!!pricingDisplay.label && (
                                <p
                                    className="m-0"
                                    style={{
                                        margin: 0,
                                        padding: 0,
                                        color: "rgba(255,255,255,0.78)",
                                        fontSize: "11px",
                                        fontWeight: 500,
                                        lineHeight: 1.2,
                                        letterSpacing: "0.08em",
                                        textTransform: "uppercase",
                                        fontFamily: "inherit",
                                        textDecoration: "none",
                                    }}
                                >
                                    {pricingDisplay.label}
                                </p>
                            )}

                            {!!pricingDisplay.value && (
                                <p
                                    className="m-0"
                                    style={{
                                        margin: 0,
                                        padding: 0,
                                        color: "#ffffff",
                                        fontSize: "14px",
                                        fontWeight: 600,
                                        lineHeight: 1.25,
                                        letterSpacing: "0",
                                        textTransform: "none",
                                        fontFamily: "inherit",
                                        textDecoration: "none",
                                        textShadow: "0 1px 2px rgba(0,0,0,0.35)",
                                    }}
                                >
                                    {pricingDisplay.value}
                                </p>
                            )}
                        </div>

                        <span
                            className="
                                inline-flex
                                h-8 w-8
                                shrink-0
                                items-center justify-center
                                rounded-full
                                bg-transparent
                                text-white
                                opacity-70
                                transition-all duration-300
                                group-hover:translate-x-0.5
                                group-hover:opacity-100
                            "
                            style={{
                                backdropFilter: "blur(8px)",
                                WebkitBackdropFilter: "blur(8px)",
                            }}
                        >
                            <ArrowRight className="h-4 w-4" strokeWidth={2.5} />
                        </span>
                    </div>
                </div>
            </div>
        </Link>
    );
}

const ProductItemTiles = ({
                              items,
                              Link,
                              title,
                              eyebrow,
                              description,
                              tileClasses,
                              filter,
                              emptyTitle,
                              emptyDescription,
                              style,
                              ctaText,
                              ctaUrl,
                          }: ProductItemTilesProps) => {
    const filteredItems = applyProductCollectionFilter(items, filter);
    const token = style ? productItemTilesTokens[style] : undefined;
    const safeCtaUrl = isValidCtaUrl(ctaUrl) ? ctaUrl?.trim() : "";
    const shouldShowCta = !!safeCtaUrl;
    const resolvedCtaText = getCtaText(ctaText, title);

    return (
        <section style={{ ...productItemTilesBaseStyles.section, ...(token?.section ?? {}) }}>
            {token ? <div aria-hidden="true" style={token.accentLine} /> : null}

            {eyebrow || title || description ? (
                <div style={productItemTilesBaseStyles.header}>
                    {eyebrow ? (
                        <p style={{ ...productItemTilesBaseStyles.eyebrow, ...(token?.eyebrow ?? {}) }}>
                            {eyebrow}
                        </p>
                    ) : null}

                    {title || token ? (
                        <div style={productItemTilesBaseStyles.titleRow}>
                            {title ? (
                                <h2 style={{ ...productItemTilesBaseStyles.title, ...(token?.title ?? {}) }}>
                                    {title}
                                </h2>
                            ) : null}

                            {token ? (
                                <span style={token.badge}>{getBadgeText(style)}</span>
                            ) : null}
                        </div>
                    ) : null}

                    {description ? (
                        <p
                            style={{
                                ...productItemTilesBaseStyles.description,
                                ...(token?.description ?? {}),
                            }}
                        >
                            {description}
                        </p>
                    ) : null}
                </div>
            ) : null}

            {filteredItems.length === 0 ? (
                <ProductEmptyState title={emptyTitle} description={emptyDescription} />
            ) : (
                <div style={productItemTilesBaseStyles.grid}>
                    {filteredItems.map((item) => (
                        <div
                            key={item.id}
                            className={tileClasses ?? ""}
                            style={productItemTilesBaseStyles.itemWrap}
                        >
                            <ProductItemTile item={item} Link={Link} />
                        </div>
                    ))}
                </div>
            )}

            {shouldShowCta ? (
                <div style={token?.ctaWrap ?? productItemTilesNeutralStyles.ctaWrap}>
                    <Link href={safeCtaUrl} className="product-item-tiles-cta-link">
                        <span style={token?.ctaButton ?? productItemTilesNeutralStyles.ctaButton}>
                            {resolvedCtaText}
                            <ArrowRight size={16} strokeWidth={2.5} />
                        </span>
                    </Link>
                </div>
            ) : null}
        </section>
    );
};

export default ProductItemTiles;